import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";

import Header from "../../../components/Header";
import { formatLkr } from "../hpcMembershipPricing";
import {
  downloadPromotionReport,
  durationLabel,
  errorMessage,
  getPromotionReport,
  listPromotions,
} from "./promotionApi";
import { Section, StatTile, usePromotionStyles } from "./promotionStyles";

const BREAKDOWNS = [
  ["package_category", "By package"],
  ["duration_code", "By duration"],
  ["member_type", "By member type"],
];

const breakdownLabel = (key, value) => {
  if (key === "duration_code") return durationLabel(value);
  if (value === "family") return key === "member_type" ? "Family (all ages)" : "Family";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "—";
};

const HpcPromotionReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const styles = usePromotionStyles();
  const { colors } = styles;
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    promotion_id: searchParams.get("promotion_id") || "",
  });
  const [promotions, setPromotions] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    [filters],
  );

  useEffect(() => {
    listPromotions({}).then(setPromotions).catch(() => setPromotions([]));
  }, []);

  useEffect(() => {
    setError(null);
    getPromotionReport(params())
      .then(setReport)
      .catch((err) => setError(errorMessage(err, "Could not load the promotion report")));
  }, [params]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadPromotionReport(params());
      toast.success("Promotion report downloaded");
    } catch (err) {
      toast.error(errorMessage(err, "Could not export the report"));
    } finally {
      setExporting(false);
    }
  };

  const set = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));

  const countTiles = report
    ? [
        ["Validation attempts", report.validation_attempts, colors.blueAccent[400]],
        ["Registrations with code", report.registrations_with_code, colors.blueAccent[400]],
        ["Redeemed", report.redeemed, colors.greenAccent[500]],
        ["Pending", report.pending, "#f5a623"],
        ["Cancelled", report.cancelled, colors.grey[500]],
        ["Reversed", report.reversed, colors.redAccent[500]],
        ["Needs reconciliation", report.needs_reconciliation, "#e65100"],
      ]
    : [];
  const moneyTiles = report
    ? [
        ["Total original value", report.total_base_value, colors.blueAccent[400]],
        ["Total discount", report.total_discount, colors.greenAccent[500]],
        ["Total paid", report.total_paid, colors.greenAccent[500]],
      ]
    : [];

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={styles.pageSx}>
      <Header title="Promotion report" subtitle="Promotion use on new registrations (Sri Lanka dates)" />

      <Section
        title="Filters"
        colors={colors}
        actions={(
          <>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} sx={styles.neutralButtonSx} onClick={() => navigate("/hpc/promotions")}>
              Back to promotions
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={styles.primaryButtonSx}
              onClick={exportCsv}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </>
        )}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={set("from")} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={set("to")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="report-promotion-label">Promotion</InputLabel>
              <Select labelId="report-promotion-label" label="Promotion" value={filters.promotion_id} onChange={set("promotion_id")}>
                <MenuItem value="">All promotions</MenuItem>
                {promotions.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.code} · {p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Section>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {report && (
        <>
          <Section title="Summary" subtitle="Money totals count redeemed promotions only" colors={colors}>
            <Grid container spacing={1.5}>
              {countTiles.map(([label, value, color]) => (
                <Grid item xs={6} sm={4} md={3} key={label}>
                  <StatTile label={label} value={value} color={color} colors={colors} />
                </Grid>
              ))}
              {moneyTiles.map(([label, value, color]) => (
                <Grid item xs={12} sm={4} key={label}>
                  <StatTile label={label} value={formatLkr(value)} color={color} colors={colors} />
                </Grid>
              ))}
            </Grid>
          </Section>

          <Grid container spacing={2}>
            {BREAKDOWNS.map(([key, title]) => (
              <Grid item xs={12} lg={4} key={key}>
                <Section title={title} colors={colors}>
                  {(report.breakdown?.[key] || []).length === 0 ? (
                    <Typography variant="body2" color={colors.grey[300]}>No redeemed promotions.</Typography>
                  ) : (
                    <TableContainer sx={{ overflowX: "auto", border: `1px solid ${colors.grey[700]}`, borderRadius: 1 }}>
                      <Table size="small" sx={{ minWidth: 320, ...styles.tableSx }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Group</TableCell>
                            <TableCell align="right">Redeemed</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Paid</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.breakdown[key].map((row) => (
                            <TableRow key={row.value}>
                              <TableCell>{breakdownLabel(key, row.value)}</TableCell>
                              <TableCell align="right">{row.redeemed}</TableCell>
                              <TableCell align="right">{formatLkr(row.discount)}</TableCell>
                              <TableCell align="right">{formatLkr(row.paid)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Section>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default HpcPromotionReport;
